// Path-based server change applicator for mapState.
//
// This module is the frontend mirror of internal/game/server_change_applicator.go.
// It takes RFC-6902-style change objects ({op, path, data}) emitted by the
// backend and applies them to mapState by dispatching purely on the path,
// independent of which event type carried the change. The event processor
// calls applyChanges() unconditionally at the top of applyEventLive (and
// from applyEventToState for non-bootstrap events), so any event — known
// or unknown — that carries a known path correctly updates state without
// the per-event processor having to look for it.
//
// Keep this file in sync with internal/game/server_change_applicator.go.
// The set of paths handled here should always be a superset of what the
// backend emits; unknown paths are logged and ignored rather than throwing.

/**
 * @typedef {import('../types/jsdoc-types.js').Change} Change
 * @typedef {import('../types/jsdoc-types.js').Character} Character
 * @typedef {import('../types/jsdoc-types.js').MonsterPlacement} MonsterPlacement
 * @typedef {import('../types/jsdoc-types.js').TurnState} TurnState
 * @typedef {import('./event-processor.js').VisibilityData} VisibilityData
 *
 * mapState is structurally typed (any) here because callers include both the
 * MapStateManager singleton and plain object stubs in tests with the same
 * field shape. Adding a strict type would require either making tests use
 * real MapStateManager instances or defining a separate MapStateLike typedef.
 */

/**
 * @typedef {object} ApplyChangesOptions
 * @property {(url: string) => string} [resolveImageUrl] - Optional transform for monster
 *   placement image URLs. Used during visibility application to rewrite backend-relative
 *   image URLs to client-absolute ones. No-ops when omitted.
 */

/**
 * Apply an array of server changes to mapState. Each change is dispatched
 * on its path. Unknown paths are logged and skipped — the function never
 * throws on unrecognized input, because the stream must keep flowing even
 * when the backend emits a new path the frontend doesn't know about yet.
 *
 * @param {Change[]} changes
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} [options]
 */
export function applyChanges(changes, mapState, options = {}) {
  if (!Array.isArray(changes)) return;
  for (const change of changes) {
    applyChange(change, mapState, options);
  }
}

/**
 * Apply a single change to mapState. Exported for targeted unit tests and
 * for callers that want to apply a single synthesized change.
 *
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} [options]
 */
export function applyChange(change, mapState, options = {}) {
  if (!change || typeof change.path !== 'string') return;
  const segments = parsePath(change.path);
  if (segments.length === 0) return;

  const head = segments[0];
  const rest = segments.slice(1);

  switch (head) {
    case 'turnState':
      applyTurnStateChange(rest, change, mapState);
      return;
    case 'characters':
      applyCharactersChange(rest, change, mapState);
      return;
    case 'map':
      applyMapChange(rest, change, mapState, options);
      return;
    case 'hook':
      applyHookChange(rest, change, mapState);
      return;
    case 'waitingRoom':
      applyWaitingRoomChange(rest, change, mapState);
      return;
    case 'players':
      applyPlayersChange(rest, change, mapState);
      return;
    case 'invitedUsers':
      applyInvitedUsersChange(rest, change, mapState);
      return;
    case 'partyWiped':
      mapState.partyWiped = /** @type {boolean} */ (change.data);
      return;
    case 'objective':
      mapState.objective = /** @type {string} */ (change.data);
      return;
    case 'adventureCompleted':
      mapState.adventureCompleted = /** @type {boolean} */ (change.data);
      return;
    case 'seed':
      mapState.seed = /** @type {number|null} */ (change.data);
      return;

    // Backend-only bookkeeping fields. The frontend doesn't store these;
    // accept silently so unknown-path warnings stay quiet for them.
    case 'lastEventId':
    case 'activeMapId':
    case 'lastActionSeq':
    case 'maps':
      return;

    default:
      console.warn('[change-applicator] unknown top-level path:', change.path);
  }
}

/**
 * Parse a JSON Pointer-style path into segments, dropping the leading slash
 * and filtering any accidental empty segments.
 * @param {string} path
 * @returns {string[]}
 */
function parsePath(path) {
  return path
    .replace(/^\/+/, '')
    .split('/')
    .filter((s) => s.length > 0);
}

// === /turnState/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyTurnStateChange(segments, change, mapState) {
  if (segments.length === 0) {
    // Whole replace (or clear with null data).
    mapState.turnState = /** @type {TurnState} */ (change.data);
    return;
  }

  // Immutable deep set — walk the path, cloning each level on the way down,
  // then assign the final segment to change.data.
  /** @type {{[key: string]: *}} */
  const root = { .../** @type {{[key: string]: *}} */ (mapState.turnState || {}) };
  /** @type {{[key: string]: *}} */
  let current = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    if (!key) return;
    const existing = current[key];
    current[key] =
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { .../** @type {{[key: string]: *}} */ (existing) }
        : {};
    current = current[key];
  }
  const lastKey = segments[segments.length - 1];
  if (!lastKey) return;

  if (change.op === 'remove') {
    delete current[lastKey];
  } else {
    current[lastKey] = change.data;
  }

  mapState.turnState = /** @type {TurnState} */ (root);
}

// === /characters/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyCharactersChange(segments, change, mapState) {
  if (segments.length === 0) {
    // /characters — list-level ops.
    if (change.op === 'set' && Array.isArray(change.data)) {
      mapState.playerCharacters = /** @type {Character[]} */ (change.data);
    } else if (change.op === 'add' && change.data) {
      mapState.playerCharacters = [...mapState.playerCharacters, /** @type {Character} */ (change.data)];
    }
    return;
  }

  const characterId = segments[0];
  if (!characterId) return;
  const charIndex = mapState.playerCharacters.findIndex((c) => c.id === characterId);
  if (charIndex < 0) return;

  if (segments.length === 1) {
    // /characters/{id} — whole-entry ops.
    if (change.op === 'remove') {
      mapState.playerCharacters = mapState.playerCharacters.filter((c) => c.id !== characterId);
    }
    return;
  }

  const field = segments[1];
  const chars = [...mapState.playerCharacters];
  const existingChar = chars[charIndex];
  if (!existingChar) return;

  switch (field) {
    case 'position': {
      if (change.data == null || change.op === 'remove') {
        chars[charIndex] = /** @type {Character} */ ({
          ...existingChar,
          col: undefined,
          row: undefined,
        });
      } else {
        const { col, row, direction } = change.data;
        chars[charIndex] = /** @type {Character} */ ({
          ...existingChar,
          col,
          row,
          ...(direction !== undefined ? { direction } : {}),
        });
      }
      break;
    }

    case 'direction':
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, direction: change.data });
      break;

    case 'lastActionResult':
      chars[charIndex] = /** @type {Character} */ ({
        ...existingChar,
        lastActionResult: change.data,
      });
      break;

    case 'inventory':
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, inventory: change.data });
      break;

    case 'actions':
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, actions: change.data });
      break;

    case 'ac':
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, ac: change.data });
      break;

    case 'data': {
      // Merge the incoming snapshot on top of the existing character, but
      // preserve any frontend-formatted effects. The raw /data blob from the
      // backend carries effects in DB format (source/duration/affects)
      // which would clobber the display format set by pc:added
      // (name/description/active/icon).
      const merged = { ...existingChar, .../** @type {object} */ (change.data) };
      if (existingChar.effects && /** @type {any} */ (change.data)?.effects) {
        /** @type {any} */ (merged).effects = existingChar.effects;
      }
      chars[charIndex] = /** @type {Character} */ (merged);
      break;
    }

    case 'conditions': {
      if (segments.length < 3) return;
      const conditionName = segments[2];
      if (!conditionName) return;
      const conditions = { .../** @type {any} */ (existingChar.conditions || {}) };
      if (change.op === 'remove') {
        delete conditions[conditionName];
      } else {
        conditions[conditionName] = change.data;
      }
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, conditions });
      break;
    }

    case 'pendingExtraAttackOffer': {
      // Two-phase Cleave (#410): server attaches an offer here on a
      // triggering hit; clears it on accept / decline / any other action.
      const next = change.op === 'remove' || change.data == null ? null : change.data;
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, pendingExtraAttackOffer: next });
      break;
    }

    case 'readied': {
      // Ready action (#266): server attaches a PCReadiedState when the
      // PC chooses a trigger to wait on. Cleared when the trigger fires
      // (or at round-wrap if it never does).
      const next = change.op === 'remove' || change.data == null ? null : change.data;
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, readied: next });
      break;
    }

    case 'spellActiveEffects': {
      // Spell buffs/debuffs (Bless, Sleep, etc.) live on a separate
      // array from passive `effects`. Server emits per-spell events
      // as either an array-append (path ends `/-`) or a full-array
      // set (no trailing segment).
      const existing = /** @type {any[]} */ (/** @type {any} */ (existingChar).spellActiveEffects || []);
      let next;
      if (segments.length === 2) {
        // Full replacement — clear or set the whole array.
        next = change.op === 'remove' ? [] : (change.data ?? []);
      } else if (segments.length === 3 && segments[2] === '-' && change.op === 'add') {
        next = [...existing, change.data];
      } else {
        return;
      }
      chars[charIndex] = /** @type {Character} */ ({ ...existingChar, spellActiveEffects: next });
      break;
    }

    default:
      return;
  }

  mapState.playerCharacters = chars;
}

// === /map/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} options
 */
function applyMapChange(segments, change, mapState, options) {
  if (segments.length === 0) return;
  switch (segments[0]) {
    case 'monsterPlacements':
      applyMonsterPlacementsChange(segments.slice(1), change, mapState);
      return;
    case 'visibility':
      applyVisibilityChange(change, mapState, options);
      return;
    case 'pcStartingPositions':
      applyPCStartingPositionsChange(change, mapState);
      return;
    default:
      // Editor / bootstrap paths like /map/config are out of scope here.
      return;
  }
}

/**
 * /map/pcStartingPositions:
 *   op=remove, data={col,row} — drop the matching cell (placement claim).
 *   op=set, data=[{col,row},...] — full replacement.
 * @param {Change} change
 * @param {*} mapState
 */
function applyPCStartingPositionsChange(change, mapState) {
  if (!change.data) return;
  if (change.op === 'remove') {
    const data = /** @type {{col: number, row: number}} */ (change.data);
    mapState.pcStartingPositions = mapState.pcStartingPositions.filter(
      (p) => !(p.col === data.col && p.row === data.row),
    );
  } else {
    const list = /** @type {Array<{col: number, row: number}>} */ (change.data);
    mapState.pcStartingPositions = list.map((pos) => ({ col: pos.col, row: pos.row }));
  }
}

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyMonsterPlacementsChange(segments, change, mapState) {
  if (segments.length === 0) return;
  const placementId = segments[0];
  if (!placementId) return;
  const placementIndex = mapState.monsterPlacements.findIndex((p) => p.id === placementId);
  if (placementIndex < 0) return;

  const placements = [...mapState.monsterPlacements];
  const existing = placements[placementIndex];
  if (!existing) return;

  if (segments.length === 1) {
    // /map/monsterPlacements/{id} — whole replace/remove.
    if (change.op === 'remove') {
      placements.splice(placementIndex, 1);
      mapState.monsterPlacements = placements;
    } else if (change.data) {
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        .../** @type {object} */ (change.data),
      });
      mapState.monsterPlacements = placements;
    }
    return;
  }

  const field = segments[1];
  switch (field) {
    case 'position': {
      const pos = /** @type {{col: number, row: number}} */ (change.data || {});
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        col: pos.col,
        row: pos.row,
      });
      break;
    }
    case 'direction':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        direction: change.data,
      });
      break;
    case 'defeated':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        defeated: change.data,
      });
      break;
    case 'damageDealt':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        damageDealt: change.data,
      });
      break;
    case 'givesLight':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        givesLight: change.data,
      });
      break;
    case 'brightRadius':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        brightRadius: change.data,
      });
      break;
    case 'dimRadius':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        dimRadius: change.data,
      });
      break;
    case 'spellActiveEffects':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        spellActiveEffects: change.data,
      });
      break;
    case 'lastActionResult':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        lastActionResult: change.data,
      });
      break;
    case 'name':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        name: change.data,
      });
      break;
    case 'discoveryLabel':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        discoveryLabel: change.data,
      });
      break;
    case 'tokenSlug':
      // Equipment-switch can flip the rendered variant (e.g. goblin
      // melee → ranged armed token). Without applying tokenSlug here
      // the server emits the change but the on-screen token never
      // updates, so the goblin keeps its old silhouette while the info
      // drawer says they switched weapons.
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        tokenSlug: change.data,
      });
      break;
    case 'imageUrl':
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({
        ...existing,
        imageUrl: change.data,
      });
      break;
    case 'conditions': {
      if (segments.length < 3) return;
      const conditionName = segments[2];
      if (!conditionName) return;
      const conditions = { .../** @type {any} */ (existing.conditions || {}) };
      if (change.op === 'remove') {
        delete conditions[conditionName];
      } else {
        conditions[conditionName] = change.data;
      }
      placements[placementIndex] = /** @type {MonsterPlacement} */ ({ ...existing, conditions });
      break;
    }
    default:
      return;
  }

  mapState.monsterPlacements = placements;
}

/**
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} options
 */
function applyVisibilityChange(change, mapState, options) {
  if (!change.data) return;
  const data = /** @type {VisibilityData} */ (change.data);
  if (change.op === 'add') {
    applyVisibilityAdd(data, mapState, options);
    return;
  }
  if (change.op === 'remove') {
    applyVisibilityRemove(data, mapState);
    return;
  }
  applyVisibilitySet(data, mapState, options);
}

/**
 * @param {VisibilityData} visData
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} options
 */
function applyVisibilitySet(visData, mapState, options) {
  mapState.mapVisibility = /** @type {any} */ ({
    gridCells: visData.gridCells || [],
    monsterPlacements: visData.monsterPlacements?.map((t) => ({ ...t })) || [],
    walls: visData.walls?.map((w) => ({ ...w })) || [],
    openings: visData.openings?.map((o) => ({ ...o })) || [],
    lights: visData.lights?.map((l) => ({ ...l })) || [],
  });

  if (visData.walls && visData.walls.length > 0) {
    mapState.walls = visData.walls;
  }
  if (visData.openings && visData.openings.length > 0) {
    mapState.openings = visData.openings;
  }
  if (visData.lights && visData.lights.length > 0) {
    mapState.lights = visData.lights;
  }
  if (visData.monsterPlacements && visData.monsterPlacements.length > 0) {
    const resolve = options.resolveImageUrl;
    mapState.monsterPlacements = visData.monsterPlacements.map(
      (p) =>
        /** @type {MonsterPlacement} */ ({
          ...p,
          imageUrl: resolve && p.imageUrl ? resolve(p.imageUrl) : p.imageUrl,
        }),
    );
  }
}

/**
 * @param {VisibilityData} visData
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 * @param {ApplyChangesOptions} options
 */
function applyVisibilityAdd(visData, mapState, options) {
  if (visData.gridCells && mapState.mapVisibility) {
    const newCellMap = new Map(visData.gridCells.map((c) => [`${c.col},${c.row}`, c]));
    const updated = (mapState.mapVisibility.gridCells || []).map((c) => {
      const key = `${c.col},${c.row}`;
      if (newCellMap.has(key)) {
        const replacement = newCellMap.get(key);
        newCellMap.delete(key);
        return replacement;
      }
      return c;
    });
    for (const cell of newCellMap.values()) {
      updated.push(cell);
    }
    mapState.mapVisibility = /** @type {any} */ ({
      ...mapState.mapVisibility,
      gridCells: updated,
    });
  }
  if (visData.walls && visData.walls.length > 0) {
    mapState.walls = [...mapState.walls, ...visData.walls];
  }
  if (visData.openings && visData.openings.length > 0) {
    mapState.openings = [...mapState.openings, ...visData.openings];
  }
  if (visData.lights && visData.lights.length > 0) {
    mapState.lights = [...mapState.lights, ...visData.lights];
  }
  // The personal-visibility refresh (fanout's per-recipient overlay) is
  // authoritative for the recipient's monster set: anything not in the
  // incoming list has left their personal visibility (moved out of LOS,
  // out of adjacency, etc.) and must be DROPPED. Other op=add callers
  // emit incremental updates for individual monsters and rely on the
  // merge semantics — those don't carry personalRefresh, so we keep
  // the merge path for them.
  if (visData.personalRefresh) {
    const resolve = options.resolveImageUrl;
    const placements = (visData.monsterPlacements || []).map(
      (p) =>
        /** @type {MonsterPlacement} */ ({
          ...p,
          imageUrl: resolve && p.imageUrl ? resolve(p.imageUrl) : p.imageUrl,
        }),
    );
    mapState.monsterPlacements = placements;
    if (mapState.mapVisibility) {
      mapState.mapVisibility = /** @type {any} */ ({
        ...mapState.mapVisibility,
        monsterPlacements: visData.monsterPlacements || [],
      });
    }
  } else if (visData.monsterPlacements && visData.monsterPlacements.length > 0) {
    const resolve = options.resolveImageUrl;
    const incomingById = new Map(
      visData.monsterPlacements.map((p) => [
        p.id,
        /** @type {MonsterPlacement} */ ({
          ...p,
          imageUrl: resolve && p.imageUrl ? resolve(p.imageUrl) : p.imageUrl,
        }),
      ]),
    );
    // Merge incoming over existing so flag transitions (e.g. the
    // placeholder flip from #253 when a sensed adjacent monster
    // moves into LOS) propagate, rather than the existing entry
    // sticking with stale fields.
    const merged = mapState.monsterPlacements.map((existing) => {
      const incoming = incomingById.get(existing.id);
      if (!incoming) return existing;
      incomingById.delete(existing.id);
      return { ...existing, ...incoming };
    });
    for (const p of incomingById.values()) {
      merged.push(p);
    }
    mapState.monsterPlacements = merged;

    // Same merge for mapVisibility.monsterPlacements.
    if (mapState.mapVisibility) {
      const incomingVisById = new Map(visData.monsterPlacements.map((p) => [p.id, p]));
      const mergedVis = (mapState.mapVisibility.monsterPlacements || []).map((existing) => {
        const incoming = incomingVisById.get(existing.id);
        if (!incoming) return existing;
        incomingVisById.delete(existing.id);
        return { ...existing, ...incoming };
      });
      for (const p of incomingVisById.values()) {
        mergedVis.push(p);
      }
      mapState.mapVisibility = /** @type {any} */ ({
        ...mapState.mapVisibility,
        monsterPlacements: mergedVis,
      });
    }
  }
}

/**
 * @param {VisibilityData} visData
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyVisibilityRemove(visData, mapState) {
  if (visData.gridCells && mapState.mapVisibility) {
    const cellsToRemove = new Set(visData.gridCells.map((c) => `${c.col},${c.row}`));
    mapState.mapVisibility = /** @type {any} */ ({
      ...mapState.mapVisibility,
      gridCells: mapState.mapVisibility.gridCells.filter((c) => !cellsToRemove.has(`${c.col},${c.row}`)),
    });
  }
  if (visData.walls && visData.walls.length > 0) {
    const wallIds = new Set(visData.walls.map((w) => w.id));
    mapState.walls = mapState.walls.filter((w) => !wallIds.has(w.id));
  }
  if (visData.openings && visData.openings.length > 0) {
    const openingIds = new Set(visData.openings.map((o) => o.id));
    mapState.openings = mapState.openings.filter((o) => !openingIds.has(o.id));
  }
  if (visData.lights && visData.lights.length > 0) {
    const lightIds = new Set(visData.lights.map((l) => l.id));
    mapState.lights = mapState.lights.filter((l) => !lightIds.has(l.id));
  }
  // Monster placements stay on the map during combat so tokens don't disappear
  // mid-turn; only remove them out of combat.
  if (visData.monsterPlacements && visData.monsterPlacements.length > 0) {
    const isInCombat = !!mapState.turnState?.combatTime;
    if (!isInCombat) {
      const placementIds = new Set(visData.monsterPlacements.map((p) => p.id));
      mapState.monsterPlacements = mapState.monsterPlacements.filter((p) => !placementIds.has(p.id));
    }
  }
}

// === /hook/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyHookChange(segments, change, mapState) {
  if (segments.length === 0) {
    mapState.hook = /** @type {any} */ (change.data);
    return;
  }
  const field = segments[0];
  if (!field || !mapState.hook) return;
  mapState.hook = /** @type {any} */ ({ ...mapState.hook, [field]: change.data });
}

// === /waitingRoom/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyWaitingRoomChange(segments, change, mapState) {
  if (segments.length === 0) {
    mapState.waitingRoom = /** @type {any} */ (change.data);
    return;
  }

  if (segments[0] === 'seats' && segments.length >= 2) {
    const index = parseInt(segments[1] || '', 10);
    if (!Number.isFinite(index)) return;
    const current = /** @type {any} */ (mapState.waitingRoom);
    if (!current || !Array.isArray(current.seats)) return;
    const seats = [...current.seats];
    seats[index] = change.data;
    mapState.waitingRoom = /** @type {any} */ ({ ...current, seats });
    return;
  }

  const field = segments[0];
  if (!field || !mapState.waitingRoom) return;
  mapState.waitingRoom = /** @type {any} */ ({ ...mapState.waitingRoom, [field]: change.data });
}

// === /players/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyPlayersChange(segments, change, mapState) {
  if (segments.length === 0) {
    if (change.op === 'set' && Array.isArray(change.data)) {
      mapState.gamePlayers = change.data.map((p) => ({
        id: p.userId,
        username: p.username,
        role: p.role,
        avatarUrl: p.avatarUrl || '',
        connectionState: 'connected',
      }));
    } else if (change.op === 'add' && change.data) {
      mapState.gamePlayers = [...mapState.gamePlayers, /** @type {any} */ (change.data)];
    }
    return;
  }

  const playerId = segments[0];
  if (!playerId) return;

  if (segments.length === 1) {
    if (change.op === 'remove') {
      mapState.gamePlayers = mapState.gamePlayers.filter((p) => p.id !== playerId);
    }
    return;
  }

  const field = segments[1];
  if (field === 'connectionState') {
    mapState.gamePlayers = mapState.gamePlayers.map((p) =>
      p.id === playerId ? /** @type {any} */ ({ ...p, connectionState: change.data }) : p,
    );
  }
}

// === /invitedUsers/... ===

/**
 * @param {string[]} segments
 * @param {Change} change
 * @param {*} mapState - MapStateManager-shaped object (see typedef block)
 */
function applyInvitedUsersChange(segments, change, mapState) {
  if (segments.length === 0) {
    if (change.op === 'set' && Array.isArray(change.data)) {
      mapState.invitedUsers = /** @type {any[]} */ (change.data);
    } else if (change.op === 'add' && change.data) {
      mapState.invitedUsers = [...mapState.invitedUsers, /** @type {any} */ (change.data)];
    }
    return;
  }

  const userId = segments[0];
  if (!userId) return;
  if (change.op === 'remove') {
    mapState.invitedUsers = mapState.invitedUsers.filter((u) => u.userId !== userId);
  }
}
